import { IActionDefinitionDto, ICrewAgent, ICrewAgentDTO } from "@kuika/kuika-cl-model";

/**
 * The two derivations `ai-hub.tsx`'s landing table adds to a row, pulled out as pure functions
 * so they are testable without instantiating the whole page (Redux `connect`, drag-drop, feature
 * gates). Everything here reads fields the page already has loaded — no new backend call.
 */

/** Resolves a model id/name to the display name `ai-hub.tsx`'s `resolveModelName` already
 *  produces (its own `aiModels`/`aiActionModels` lookup + `formatModelName`). */
export type IAiHubModelNameResolver = (modelId: string) => string;

/**
 * "{n} inputs → {return type}" — the name cell's secondary line. Reads `parameters` (the
 * persisted count on the catalog row) rather than `actionParameters` (the in-progress editing
 * state `sd` keeps open only while a create/edit session is active, not something every loaded
 * row carries) and `returnTypeName`, the same string the properties panel already falls back to
 * "Object" for (`designer-property-actions.ts`).
 */
export const describeActionSignature = (action: IActionDefinitionDto): string => {
  const inputCount: number = action.parameters?.length ?? 0;
  const inputsLabel: string = inputCount === 1 ? "1 input" : `${inputCount} inputs`;
  return `${inputsLabel} → ${action.returnTypeName || "Object"}`;
};

/**
 * Every distinct model id an already-parsed AI Agent's manager and crew reference — the same
 * traversal `buildAgentModelUpdates` already does for the deprecation dialog, minus its
 * deprecated/shutdown-only filter, since the Model column shows every configured model.
 */
export const collectAgentModelRefs = (agentInfo: ICrewAgentDTO | null): string[] => {
  if (!agentInfo) return [];
  const crewModelIds: string[] = (agentInfo.crewAgents ?? [])
    .map((agent: ICrewAgent): string | undefined => agent.model)
    .filter((modelId: string | undefined): modelId is string => !!modelId);
  const refs: string[] = agentInfo.managerAgent?.model ? [...crewModelIds, agentInfo.managerAgent.model] : crewModelIds;
  return Array.from(new Set(refs));
};

/**
 * The Model column's label for an AI Agent row: the resolved name when every configured model
 * agrees, a count when they don't, an em dash when none is configured at all — an agent with no
 * model is a different fact from an unresolved reference, which `getActionCurrentModelName`
 * (AI Actions) reports as "Unknown Model" instead.
 */
export const describeAgentModel = (
  agentInfo: ICrewAgentDTO | null,
  resolveModelName: IAiHubModelNameResolver
): string => {
  const distinctIds: string[] = collectAgentModelRefs(agentInfo);
  if (distinctIds.length === 0) return "—";
  if (distinctIds.length === 1) return resolveModelName(distinctIds[0]);
  return `${distinctIds.length} models`;
};

/**
 * Whether the landing offers "New folder". A folder needs something to file and the AI Actions
 * datasource it hangs off, so on a project with neither the button could only fail — the page
 * used to show it anyway and answer the click with a warning notification.
 *
 * `itemCount` is the project's AI functions, not the list's rows: gating on the filtered set
 * would make the button vanish while the reader typed in the search box.
 */
export const canCreateAiFolder = (canManage: boolean, itemCount: number, folderDatasourceId: string): boolean =>
  canManage && itemCount > 0 && !!folderDatasourceId;
